import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { getVehicleClaimEligibleDrivers, jobListBireBirEslestir, resetVehicleClaimEligibleDrivers } from "../../../features/joblist/jobListSlice";
import { showModal } from "../../../features/modal/modalSlice";
import { useScrollLock } from "../../../hooks/useScrollLock";
import Btn from "../../shared/button/Btn";
import SmallSpinner from "../../shared/spinners/SmallSpinner";
import InputText from "../../shared/input/new/InputText";

const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 26;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.6);

  .modal {
    width: min(680px, 100%);
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    padding: 32px;
    color: ${({ theme }) => theme.mainTextColor};
    background-color: ${({ theme }) => theme.mainBgColor};
    border-radius: 10px;
  }

  h2 {
    margin: 0 0 8px;
    font-size: var(--font-size-xl);
  }

  .description {
    margin: 0 0 24px;
    color: ${({ theme }) => theme.secondaryTextColor || theme.mainTextColor};
    font-size: var(--font-size-sm);
    line-height: 20px;
  }

  .transport-set {
    margin-bottom: 20px;
    padding: 12px 14px;
    background-color: ${({ theme }) => theme.secondaryBgColor};
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 8px;
    font-size: var(--font-size-sm);
  }

  .mode-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
  }

  .mode-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 8px;
    cursor: pointer;

    &.active {
      border-color: #E85446;
      box-shadow: 0 0 0 1px #E85446;
    }

    input {
      margin-top: 3px;
    }

    strong, span {
      display: block;
    }

    span {
      margin-top: 5px;
      font-size: var(--font-size-sm);
      line-height: 18px;
      opacity: .75;
    }
  }

  .driver-field {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-bottom: 20px;

    label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-lg);
    }

    select {
      width: 100%;
      height: 42px;
      padding: 0 10px;
      color: ${({ theme }) => theme.mainTextColor};
      background-color: ${({ theme }) => theme.mainBgColor};
      border: 1px solid ${({ theme }) => theme.mainBorderColor};
      border-radius: 6px;
    }
  }

  .status-message {
    margin-bottom: 20px;
    color: #E85446;
    font-size: var(--font-size-sm);
    line-height: 19px;
  }

  .number-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }

  .field-error {
    margin-top: 6px;
    color: #E85446;
    font-size: 12px;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  @media (max-width: 700px) {
    .mode-options {
      grid-template-columns: 1fr;
    }

    .number-fields {
      grid-template-columns: 1fr;
    }
  }
`;

const JobListDriverAssignmentModal = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { lockScroll, unlockScroll } = useScrollLock();
  const [driverAssignmentMode, setDriverAssignmentMode] = useState(0);
  const [assignedSubDriverUserId, setAssignedSubDriverUserId] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [showNumberErrors, setShowNumberErrors] = useState(false);

  const { modalLocation } = useSelector((state) => state.modal);
  const {
    aracBekleyenYukCheckedItem,
    yukBekleyenAracCheckedItemList,
    vehicleClaimEligibleDrivers,
    isLoadingVehicleClaimEligibleDrivers,
    vehicleClaimEligibleDriversError,
    isLoadingJobListBireBirEslestir,
  } = useSelector((state) => state.jobList);

  const selectedDailyVehicle = yukBekleyenAracCheckedItemList.length === 1
    ? yukBekleyenAracCheckedItemList[0]
    : null;
  const selectedTruckId = selectedDailyVehicle?.truckId ?? selectedDailyVehicle?.truck?.id ?? null;
  const selectedTruckPlate = selectedDailyVehicle?.truckPlate ?? selectedDailyVehicle?.truck?.plateNumber;
  const selectedTrailerPlate = selectedDailyVehicle?.trailerPlate ?? selectedDailyVehicle?.trailer?.trailerPlate;

  useEffect(() => {
    lockScroll();
    dispatch(resetVehicleClaimEligibleDrivers());

    return () => {
      unlockScroll();
      dispatch(resetVehicleClaimEligibleDrivers());
    };
  }, [dispatch, lockScroll, unlockScroll]);

  useEffect(() => {
    if (location.pathname !== modalLocation) {
      dispatch(showModal(null));
    }
  }, [dispatch, location.pathname, modalLocation]);

  useEffect(() => {
    setDriverAssignmentMode(0);
    setAssignedSubDriverUserId("");
    setContainerNumber("");
    setSealNumber("");
    setShowNumberErrors(false);
    dispatch(resetVehicleClaimEligibleDrivers());
  }, [dispatch, selectedDailyVehicle?.id]);

  useEffect(() => {
    if (driverAssignmentMode !== 1 || !selectedTruckId) {
      dispatch(resetVehicleClaimEligibleDrivers());
      return undefined;
    }

    const request = dispatch(getVehicleClaimEligibleDrivers(selectedTruckId));
    return () => request.abort();
  }, [dispatch, driverAssignmentMode, selectedTruckId]);

  const handleModeChange = (mode) => {
    setDriverAssignmentMode(mode);
    setAssignedSubDriverUserId("");
  };

  const handleClose = () => {
    if (!isLoadingJobListBireBirEslestir) {
      dispatch(showModal(null));
    }
  };

  const handleSubmit = () => {
    if (!aracBekleyenYukCheckedItem || !selectedDailyVehicle || !selectedTruckId) return;
    if (!containerNumber.trim() || !sealNumber.trim()) {
      setShowNumberErrors(true);
      return;
    }

    dispatch(jobListBireBirEslestir({
      yukId: aracBekleyenYukCheckedItem.id,
      gunlukAracId: selectedDailyVehicle.id,
      driverAssignmentMode,
      assignedSubDriverUserId:
        driverAssignmentMode === 1 && assignedSubDriverUserId
          ? assignedSubDriverUserId
          : null,
      containerNumber,
      sealNumber,
    }));
  };

  const vehicleClaimUnavailable = driverAssignmentMode === 1 && (
    !selectedTruckId ||
    isLoadingVehicleClaimEligibleDrivers ||
    Boolean(vehicleClaimEligibleDriversError) ||
    vehicleClaimEligibleDrivers.length === 0
  );
  const submitDisabled = isLoadingJobListBireBirEslestir || !selectedTruckId || vehicleClaimUnavailable;

  return (
    <ModalContainer onClick={handleClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <h2>Yük atama şekli</h2>
        <p className="description">Seçilen yükün doğrudan sürücüye mi, yoksa mobil sürücünün teslim alacağı taşıma setine mi atanacağını seçin.</p>

        <div className="transport-set">
          <strong>Seçili taşıma seti: </strong>
          {selectedTruckPlate || "Geçerli araç/plaka bilgisi bulunamadı"}
          {selectedTrailerPlate ? ` / Dorse: ${selectedTrailerPlate}` : ""}
        </div>

        <div className="number-fields">
          <div>
            <InputText name="containerNumber" labelText="Konteyner No*" data={containerNumber} setData={(event) => setContainerNumber(event.target.value)} inputWidth="100%" />
            {showNumberErrors && !containerNumber.trim() && <div className="field-error">Konteyner numarası zorunludur.</div>}
          </div>
          <div>
            <InputText name="sealNumber" labelText="Mühür No*" data={sealNumber} setData={(event) => setSealNumber(event.target.value)} inputWidth="100%" />
            {showNumberErrors && !sealNumber.trim() && <div className="field-error">Mühür numarası zorunludur.</div>}
          </div>
        </div>

        <div className="mode-options">
          <label className={`mode-option ${driverAssignmentMode === 0 ? "active" : ""}`}>
            <input
              type="radio"
              name="driverAssignmentMode"
              checked={driverAssignmentMode === 0}
              onChange={() => handleModeChange(0)}
            />
            <div>
              <strong>Doğrudan sürücüye ata</strong>
              <span>Yük, günlük araç kaydındaki mobil sürücüye atanır.</span>
            </div>
          </label>

          <label className={`mode-option ${driverAssignmentMode === 1 ? "active" : ""}`}>
            <input
              type="radio"
              name="driverAssignmentMode"
              checked={driverAssignmentMode === 1}
              disabled={!selectedTruckId}
              onChange={() => handleModeChange(1)}
            />
            <div>
              <strong>Taşıma setine ata</strong>
              <span>Uygun mobil alt sürücü taşıma setini uygulamadan teslim alır.</span>
            </div>
          </label>
        </div>

        {driverAssignmentMode === 1 && selectedTruckId && (
          <div className="driver-field">
            <label htmlFor="assignedSubDriverUserId">Teslim alabilecek mobil sürücü</label>
            <select
              id="assignedSubDriverUserId"
              value={assignedSubDriverUserId}
              disabled={isLoadingVehicleClaimEligibleDrivers || vehicleClaimEligibleDrivers.length === 0}
              onChange={(event) => setAssignedSubDriverUserId(event.target.value)}
            >
              <option value="">Tüm uygun sürücüler teslim alabilir</option>
              {vehicleClaimEligibleDrivers.map((driver) => (
                <option key={driver.userId} value={driver.userId}>
                  {driver.name}{driver.phoneNumber ? ` - ${driver.phoneNumber}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {!selectedTruckId && <div className="status-message">Bu kayıtta geçerli araç bulunmuyor.</div>}
        {driverAssignmentMode === 1 && isLoadingVehicleClaimEligibleDrivers && <div className="status-message">Uygun mobil sürücüler yükleniyor...</div>}
        {driverAssignmentMode === 1 && vehicleClaimEligibleDriversError && <div className="status-message">{vehicleClaimEligibleDriversError}</div>}
        {driverAssignmentMode === 1 && selectedTruckId && !isLoadingVehicleClaimEligibleDrivers && !vehicleClaimEligibleDriversError && vehicleClaimEligibleDrivers.length === 0 && (
          <div className="status-message">Bu aracın master kullanıcısına bağlı mobil alt sürücü bulunamadı.</div>
        )}

        <div className="actions">
          <Btn width="130px" color="flex" handleClick={handleClose} isDisabled={isLoadingJobListBireBirEslestir}>Vazgeç</Btn>
          <Btn width="220px" bgColor="#E85446" color="#fff" handleClick={handleSubmit} isDisabled={submitDisabled}>
            {isLoadingJobListBireBirEslestir
              ? <SmallSpinner size="20px" color="#fff" bgColor="#E85446"/>
              : driverAssignmentMode === 1 ? "Taşıma Setine Ata" : "Sürücüye Ata"}
          </Btn>
        </div>
      </div>
    </ModalContainer>
  );
};

export default JobListDriverAssignmentModal;
